/**
 * The single source of truth for what must never reach Logfire.
 *
 * Every runtime — browser RUM, the Node span processor, the edge helpers and
 * the Mastra exporter formatter — consumes THIS file. Keeping the rules as data
 * rather than behaviour is what stops the four implementations drifting apart.
 *
 * Firmable's bar is strict: no emails, phone numbers, contact names, or
 * request/response bodies. Only ids, counts, statuses, durations and enums.
 */

/** Lowercase and strip separators so `work_email`, `workEmail` and `work-email` all match. */
export const normaliseKey = (key: string): string => key.toLowerCase().replace(/[\s_\-.]/g, '');

/**
 * TIER 0 — SAFE keys. Never value-scanned, never dropped.
 *
 * Load-bearing: without it the phone regexes eat numeric profile ids, and the
 * `name` deny rule would strip `service.name`.
 */
export const SAFE_KEY_PATTERNS: RegExp[] = [
  /^firmable\./,
  /^(service|deployment|telemetry|process|host|otel|cloud)\./,
  /^logfire\.(span_type|level_num|msg_template|json_schema|tags|scrubbed)$/,
  /^(id|ids)$/,
  /(^|\.)(id|ids)$/,
  /_id$/,
  /_ids$/,
  /^(count|total|size|limit|offset|page|index|length|from)$/,
  /_count$/,
  /_length$/,
  /^(status|statuscode|code|level|kind|type|method|scheme|outcome)$/,
  /^http\.(request|response)\.(method|status_code|resend_count)$/,
  /^http\.route$/,
  // Infrastructure attributes whose last segment is `name`. Without these the
  // Tier 1 `name` deny rule redacts the hostname of every outbound call and
  // both attributes @vercel/otel derives for Datadog, which is how a
  // dev-search.firmable.com span ends up reading `[fbl-redacted:name]`.
  /^net\./,
  /^(operation|resource)\.name$/,
  /^http\.(client|server)\.name$/,
  /^next\./,
  /^(duration|elapsed|latency|ms)$/,
  /^(start|end)(time|timestamp)$/,
  /^url\.(scheme|path|template|query_keys)$/,
  /^(server|network)\./,
  // NOT `error.` wholesale: error.message is precisely where PII leaks
  // ("no profile found for jane@acme.com"), so it must be value-scanned.
  /^error\.type$/,
  /^web_vital\./,
  /^session\.id$/,
  /^user\.id$/,
  /^inngest\.(run_id|function|event|attempt|step)/,
];

/**
 * TIER 1 — HARD DENY by key. The value is replaced outright.
 *
 * Matched against both the full dotted key and its last segment. Note the
 * plural variants: Mastra's own filter matches keys exactly after
 * normalisation, so `email` does NOT catch `emails` — which is the field
 * `reveal_person_email` actually returns.
 */
export const DENY_KEYS: { re: RegExp; reason: string }[] = [
  // Contact PII — the strict bar.
  { re: /^(email|emails|emailaddress|workemail|personalemail|primaryemail|mail|mailaddress)$/, reason: 'email' },
  { re: /email/, reason: 'email' },
  { re: /^(phone|phones|phonenumber|mobile|mobilenumber|telephone|tel|fax|directdial)$/, reason: 'phone' },
  { re: /phone|mobilenumber|directdial/, reason: 'phone' },
  {
    re: /^(name|fullname|firstname|lastname|givenname|familyname|surname|middlename|displayname|contactname|personname|username|nickname)$/,
    reason: 'name',
  },
  { re: /^(address|streetaddress|street|addressline1|addressline2|postalcode|postcode|zip|zipcode)$/, reason: 'address' },
  { re: /^(dob|dateofbirth|birthdate|birthday|age|gender)$/, reason: 'demographic' },
  // A LinkedIn URN or slug uniquely identifies a natural person.
  { re: /^(lnurl|lnslug|linkedin|linkedinurl|linkedinurn|linkedinslug|profileurl|publicidentifier|handle)$/, reason: 'social-identity' },
  // Free text users type — routinely contains company and person names.
  { re: /^(q|query|searchterm|searchquery|keyword|keywords|term|prompt|completion|note|notes|comment|description)$/, reason: 'free-text' },
  // Secrets. These should never be attributes at all; this is belt and braces.
  { re: /^(password|passwd|pwd|secret|clientsecret|privatekey|apikey|apisecret)$/, reason: 'secret' },
  {
    re: /^(token|accesstoken|refreshtoken|idtoken|sessiontoken|bearertoken|jwt|authorization|auth|bearer|credential|credentials|cookie|setcookie|signature)$/,
    reason: 'secret',
  },
];

/**
 * TIER 2 — payload roots. Replaced by a shape summary rather than redacted in
 * place, because walking a body and scrubbing it can never be proven complete.
 * Describing it can.
 */
export const BODY_ROOT_KEYS: RegExp[] = [
  /^(body|requestbody|responsebody|payload|rawbody|request|response)$/,
  /^(input|output|result|results|data)$/,
  /^(items|records|rows|profiles|people|persons|companies|contacts|entities|hits|documents)$/,
  /^(arguments|args|params|parameters|variables|messages|filters|filter)$/,
  /^http\.(request|response)\.body$/,
];

/** Keys whose free text becomes a stable hash + length, so repeat queries stay correlatable. */
export const HASH_KEYS: RegExp[] = [/^(q|query|searchterm|searchquery|term|keyword)$/];

/**
 * TIER 3 — value regexes, applied to every string that survives the key rules.
 * Ordered most specific first. Firmable's market is AU/NZ plus US/CA.
 */
export const VALUE_PATTERNS: { name: string; re: RegExp }[] = [
  { name: 'jwt', re: /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{6,}\b/g },
  { name: 'stripe-key', re: /\b(?:sk|pk)_(?:test|live)_[A-Za-z0-9]{16,}\b/g },
  { name: 'fbl-key', re: /\bfbl_[A-Za-z0-9]{12,}\b/g },
  { name: 'logfire-token', re: /\bpylf_v\d+_[a-z]{2}_[A-Za-z0-9_]{16,}\b/g },
  { name: 'aws-key', re: /\b(?:AKIA|ASIA)[0-9A-Z]{16}\b/g },
  { name: 'email', re: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,24}\b/g },
  { name: 'phone-au-mobile', re: /(?:\+?61[ .-]?|\b0)4\d{2}[ .-]?\d{3}[ .-]?\d{3}\b/g },
  { name: 'phone-au-fixed', re: /(?:\+?61[ .-]?|\b\(?0)[2378]\)?[ .-]?\d{4}[ .-]?\d{4}\b/g },
  { name: 'phone-au-service', re: /\b1(?:300|800)[ .-]?\d{3}[ .-]?\d{3}\b|\b13[ .-]?\d{2}[ .-]?\d{2}\b/g },
  { name: 'phone-nz-mobile', re: /(?:\+?64[ .-]?|\b0)2\d[ .-]?\d{3,4}[ .-]?\d{3,4}\b/g },
  { name: 'phone-nanp', re: /(?:\+?1[ .-]?)?\(?[2-9]\d{2}\)?[ .-]?[2-9]\d{2}[ .-]?\d{4}\b/g },
  { name: 'phone-e164', re: /\+\d{1,3}[ .-]?\d[\d .()-]{6,16}\d\b/g },
];

/** Keys holding a URL. Sanitised rather than redacted — the path is useful, the query is not. */
export const URL_KEY_PATTERN = /(^|[._])(url|uri|href|location|referer|referrer|endpoint|target)$|^url\.|^http\.url$|^logfire\.page\.url/i;

/** Query params safe to keep. Everything else is dropped, values included. */
export const QUERY_PARAM_ALLOWLIST = new Set([
  'from', 'size', 'limit', 'offset', 'page', 'country', 'source', 'identifier',
  'category', 'tab', 'view', 'sort', 'order', 'type', 'transport',
]);

/** Path segments matching any of these collapse to `:id`. */
export const ID_SEGMENT_PATTERNS: RegExp[] = [
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  /^[0-9a-f]{24,32}$/i,
  /^\d{4,}$/,
  /^[A-Za-z0-9_-]{20,}$/,
  /^(user|org|sess|list|cmp|prs)_[A-Za-z0-9]{6,}$/i,
  // Firmable profile ids: `f000001729377` for companies, `fp000004932930` for
  // people. The most common ids in our URLs and the ones the generic patterns
  // above all miss — too short for the 20+ and 24+ rules, and not purely
  // numeric.
  //
  // The prefix is 1-2 letters. It was `[a-z]` until `fp` ids were found
  // untemplated in live span names (`GET /internal/people/fp000520953664` on
  // web, and the same from the extension), so a person id was reaching the
  // indexed span name verbatim. No real path segment is one or two letters
  // followed by eight digits.
  /^[a-z]{1,2}\d{8,}$/i,
];

export const REDACTED = (reason: string): string => `[fbl-redacted:${reason}]`;

/** Logfire truncates names and messages at 512 bytes; align so nothing is silently cut. */
export const MAX_STRING_LENGTH = 512;
export const MAX_DEPTH = 6;
export const MAX_ARRAY_ITEMS = 50;
